// MissionReady Radio — live audio transport (browser WebRTC mesh).
//
// Audio is WebRTC media only. Supabase Realtime (the existing infrastructure)
// carries signaling and presence exclusively — never audio. No TURN, no SFU,
// no video, no 1:1 calling, no audio storage.
//
// One-to-many: the transmitter offers an audio-only peer connection to every
// authorized participant currently present on the site's Radio presence topic.
// The transmitter never picks recipients; presence membership is gated by the
// existing site authorization (the Radio thread and its RLS), and the database
// remains the authority.
import { supabase } from "@/integrations/supabase/client";
import {
  RADIO_SIGNAL_EVENT,
  radioPresenceTopic,
  radioSignalTopic,
} from "@/lib/radio";
import type { RadioParticipant } from "./radio-state";
import { isVoiceFocusBusy } from "./radio-mic-bus";

type Envelope =
  | { type: "webrtc-offer"; from: string; to: string; sdp: string }
  | { type: "webrtc-answer"; from: string; to: string; sdp: string }
  | { type: "webrtc-ice"; from: string; to: string; candidate: RTCIceCandidateInit }
  | { type: "transmit-start"; from: string; at: string }
  | { type: "transmit-end"; from: string; at: string };

const ICE: RTCConfiguration = {
  // Public STUN only. TURN is intentionally NOT configured: an unestablished
  // peer connection surfaces as a Radio connection error instead.
  iceServers: [{ urls: ["stun:stun.l.google.com:19302", "stun:stun1.l.google.com:19302"] }],
};

export type RadioSessionCallbacks = {
  onParticipants: (list: RadioParticipant[]) => void;
  onRemoteTransmission: (userId: string, active: boolean) => void;
  onConnectionError: (message: string) => void;
  onPlaybackBlocked?: (blocked: boolean) => void;
};

export class RadioSession {
  private signal: ReturnType<typeof supabase.channel> | null = null;
  private presence: ReturnType<typeof supabase.channel> | null = null;
  private outbound = new Map<string, RTCPeerConnection>();
  private inbound = new Map<string, RTCPeerConnection>();
  private audioEls = new Map<string, HTMLAudioElement>();
  private localStream: MediaStream | null = null;
  private destroyed = false;
  private resumeAudio = () => { void this.enableAudio(); };

  constructor(
    private readonly channelId: string,
    private readonly userId: string,
    private readonly displayName: string,
    private readonly cb: RadioSessionCallbacks,
  ) {}

  get participants(): string[] {
    return Array.from(new Set(this.presenceIds));
  }

  private presenceIds: string[] = [];

  /** Join signaling + presence for this Radio thread (idempotent per instance). */
  async connect(): Promise<void> {
    if (this.signal || this.presence) return;
    document.addEventListener("pointerdown", this.resumeAudio);

    const signal = supabase.channel(radioSignalTopic(this.channelId), {
      config: { broadcast: { self: false } },
    });
    signal.on("broadcast", { event: RADIO_SIGNAL_EVENT }, ({ payload }) => {
      void this.onSignal(payload as Envelope);
    });
    this.signal = signal;

    const presence = supabase.channel(radioPresenceTopic(this.channelId), {
      config: { presence: { key: this.userId } },
    });
    presence.on("presence", { event: "sync" }, () => this.emitPresence());
    presence.on("presence", { event: "join" }, () => this.emitPresence());
    presence.on("presence", { event: "leave" }, () => this.emitPresence());
    this.presence = presence;

    await Promise.all([
      new Promise<void>((resolve, reject) => {
        signal.subscribe((status) => {
          if (status === "SUBSCRIBED") resolve();
          else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT")
            reject(new Error("Radio connection unavailable"));
        });
      }),
      new Promise<void>((resolve, reject) => {
        presence.subscribe((status) => {
          if (status === "SUBSCRIBED") {
            void presence
              .track({ userId: this.userId, name: this.displayName, state: "available" })
              .then(() => resolve());
          } else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
            reject(new Error("Radio connection unavailable"));
          }
        });
      }),
    ]);
  }

  private emitPresence() {
    const st = (this.presence?.presenceState() ?? {}) as Record<string, any[]>;
    const list: RadioParticipant[] = [];
    for (const entries of Object.values(st)) {
      const e = entries?.[0] ?? {};
      if (!e.userId) continue;
      list.push({
        userId: String(e.userId),
        name: String(e.name ?? "Officer"),
        state: (e.state as RadioParticipant["state"]) ?? "connected",
      });
    }
    this.presenceIds = list.map((p) => p.userId);
    this.cb.onParticipants(list);
  }

  private send(env: Envelope) {
    this.signal?.send({ type: "broadcast", event: RADIO_SIGNAL_EVENT, payload: env });
  }

  private async trackState(state: RadioParticipant["state"]) {
    try {
      await this.presence?.track({ userId: this.userId, name: this.displayName, state });
    } catch {
      /* presence is ephemeral; ignore */
    }
  }

  /**
   * Start transmitting to every authorized participant currently present.
   * Returns the live microphone stream so the caller can also feed Deepgram.
   */
  async startTransmission(): Promise<MediaStream> {
    if (isVoiceFocusBusy()) throw new Error("mic-unavailable");
    this.audioEls.forEach((el) => { el.muted = true; });
    if (!navigator.mediaDevices?.getUserMedia) {
      const err = new Error("mic-unavailable");
      throw err;
    }
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      video: false,
    });
    this.localStream = stream;
    await this.trackState("transmitting");
    this.send({ type: "transmit-start", from: this.userId, at: new Date().toISOString() });

    const peers = this.participants.filter((id) => id !== this.userId);
    let anyFailed = false;
    for (const peerId of peers) {
      try {
        await this.offerTo(peerId, stream);
      } catch {
        anyFailed = true;
      }
    }
    if (anyFailed && peers.length) this.cb.onConnectionError("Radio connection unavailable");
    return stream;
  }

  private async offerTo(peerId: string, stream: MediaStream) {
    this.closeOutbound(peerId);
    const pc = new RTCPeerConnection(ICE);
    this.outbound.set(peerId, pc);
    stream.getAudioTracks().forEach((t) => pc.addTrack(t, stream));
    pc.onicecandidate = (e) => {
      if (e.candidate)
        this.send({
          type: "webrtc-ice",
          from: this.userId,
          to: peerId,
          candidate: e.candidate.toJSON(),
        });
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === "failed") {
        this.cb.onConnectionError("Radio connection unavailable");
        this.closeOutbound(peerId);
      }
    };
    const offer = await pc.createOffer({ offerToReceiveAudio: false });
    await pc.setLocalDescription(offer);
    this.send({ type: "webrtc-offer", from: this.userId, to: peerId, sdp: offer.sdp ?? "" });
  }

  private async onSignal(env: Envelope) {
    if (this.destroyed || !env || env.from === this.userId) return;
    switch (env.type) {
      case "transmit-start":
        this.cb.onRemoteTransmission(env.from, true);
        return;
      case "transmit-end":
        this.cb.onRemoteTransmission(env.from, false);
        this.closeInbound(env.from);
        return;
      case "webrtc-offer": {
        if (env.to !== this.userId) return;
        this.closeInbound(env.from);
        const pc = new RTCPeerConnection(ICE);
        this.inbound.set(env.from, pc);
        pc.ontrack = (e) => this.playRemote(env.from, e.streams[0]);
        pc.onicecandidate = (e) => {
          if (e.candidate)
            this.send({
              type: "webrtc-ice",
              from: this.userId,
              to: env.from,
              candidate: e.candidate.toJSON(),
            });
        };
        pc.onconnectionstatechange = () => {
          if (pc.connectionState === "failed") this.closeInbound(env.from);
        };
        try {
          await pc.setRemoteDescription({ type: "offer", sdp: env.sdp });
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          this.send({
            type: "webrtc-answer",
            from: this.userId,
            to: env.from,
            sdp: answer.sdp ?? "",
          });
        } catch {
          this.closeInbound(env.from);
          this.cb.onConnectionError("Radio connection unavailable");
        }
        return;
      }
      case "webrtc-answer": {
        if (env.to !== this.userId) return;
        const pc = this.outbound.get(env.from);
        if (!pc) return;
        try {
          await pc.setRemoteDescription({ type: "answer", sdp: env.sdp });
        } catch {
          this.closeOutbound(env.from);
        }
        return;
      }
      case "webrtc-ice": {
        if (env.to !== this.userId) return;
        const pc = this.outbound.get(env.from) ?? this.inbound.get(env.from);
        if (!pc) return;
        try {
          await pc.addIceCandidate(env.candidate);
        } catch {
          /* ignore late candidates */
        }
        return;
      }
    }
  }

  private playRemote(peerId: string, stream?: MediaStream) {
    if (!stream || typeof document === "undefined") return;
    let el = this.audioEls.get(peerId);
    if (!el) {
      el = document.createElement("audio");
      el.autoplay = true;
      el.muted = !!this.localStream;
      (el as any).playsInline = true;
      el.style.display = "none";
      document.body.appendChild(el);
      this.audioEls.set(peerId, el);
    }
    el.srcObject = stream;
    const target = el;
    void target.play().catch(() => this.cb.onPlaybackBlocked?.(true));
  }

  async enableAudio(): Promise<void> {
    try {
      await Promise.all(Array.from(this.audioEls.values(), (el) => el.play()));
      this.cb.onPlaybackBlocked?.(false);
    } catch { this.cb.onPlaybackBlocked?.(true); }
  }

  /** Stop the outbound transmission; the Radio context stays connected. */
  async stopTransmission(): Promise<void> {
    this.send({ type: "transmit-end", from: this.userId, at: new Date().toISOString() });
    this.localStream?.getTracks().forEach((t) => t.stop());
    this.localStream = null;
    this.audioEls.forEach((el) => { el.muted = false; });
    for (const id of Array.from(this.outbound.keys())) this.closeOutbound(id);
    await this.trackState("available");
  }

  private closeOutbound(peerId: string) {
    const pc = this.outbound.get(peerId);
    if (pc) {
      try {
        pc.close();
      } catch {
        /* ignore */
      }
      this.outbound.delete(peerId);
    }
  }

  private closeInbound(peerId: string) {
    const pc = this.inbound.get(peerId);
    if (pc) {
      try {
        pc.close();
      } catch {
        /* ignore */
      }
      this.inbound.delete(peerId);
    }
    const el = this.audioEls.get(peerId);
    if (el) {
      el.srcObject = null;
      el.remove();
      this.audioEls.delete(peerId);
    }
  }

  /** Full teardown: media, peers, and both Realtime subscriptions. */
  async destroy(): Promise<void> {
    if (this.destroyed) return;
    this.destroyed = true;
    document.removeEventListener("pointerdown", this.resumeAudio);
    this.localStream?.getTracks().forEach((t) => t.stop());
    this.localStream = null;
    for (const id of Array.from(this.outbound.keys())) this.closeOutbound(id);
    for (const id of Array.from(this.inbound.keys())) this.closeInbound(id);
    try {
      if (this.presence) {
        await this.presence.untrack();
        await supabase.removeChannel(this.presence);
      }
    } catch {
      /* ignore */
    }
    try {
      if (this.signal) await supabase.removeChannel(this.signal);
    } catch {
      /* ignore */
    }
    this.presence = null;
    this.signal = null;
  }
}
